/* ===========================================================
   panels/Palette.jsx — the search menu (Ctrl/⌘ K, or the 🔍 button)

   One box for everything: docs (by title and by what's in them, from
   this device's copy, so it works offline), headings in the open doc,
   and actions (new doc, share, save a version, insert a table…).
   Arrow keys + Enter, or click. Opens instantly: no network calls.
   =========================================================== */

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { docs, docContext, mode, session, updateProfile } from '../state.js';
import { go } from '../lib/router.js';
import { createDoc } from '../sync/docs.js';
import { headingsOf } from '../../shared/blocks.js';
import { toJSON } from '../editor/convert.js';
import { askLink, insertEquation, addPicture } from '../editor/Toolbar.jsx';
import { newSandbox } from '../editor/nodes/sandbox.js';
import { makeEditCopy } from './History.jsx';
import { Icon, toast } from '../ui/ui.jsx';
import { w } from '../lib/words.js';
import { emptyDoc } from '../../shared/blocks.js';

function score(q, text) {
  if (!q) return 1;
  const t = String(text || '').toLowerCase();
  let s = 0;
  for (const word of q.split(/\s+/).filter(Boolean)) {
    const i = t.indexOf(word);
    if (i < 0) return 0;
    s += i === 0 ? 3 : /\W/.test(t[i - 1] || ' ') ? 2 : 1;
  }
  return s;
}

function snippetAround(text, q) {
  const t = String(text || '');
  const i = t.toLowerCase().indexOf(q.split(/\s+/)[0] || '');
  if (i < 0) return '';
  const start = Math.max(0, i - 40);
  return `${start ? '…' : ''}${t.slice(start, i + 80).replace(/\s+/g, ' ')}…`;
}

export function Palette({ onClose }) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const input = useRef();
  const listRef = useRef();
  const ctx = docContext.value;
  const dev = mode.value === 'dev';
  const ready = session.value.status === 'ready';

  useEffect(() => {
    input.current?.focus();
    const before = document.activeElement;
    return () => before?.focus?.();
  }, []);

  const items = useMemo(() => {
    const query = q.trim().toLowerCase();
    const out = [];
    const ed = ctx?.editor;
    const hasSel = ed && !ed.state.selection.empty;
    const can = ctx?.canEdit && ed;

    const actions = [
      hasSel && can && { label: 'Add a link to the selected text', icon: 'link', run: () => askLink(ed) },
      ready && { label: 'New document', icon: 'plus', hint: 'Blank', run: async () => go(`/d/${await createDoc({ json: emptyDoc() })}`) },
      ctx && ctx.role === 'owner' && { label: 'Share this doc', icon: 'share', run: () => ctx.openPanel('share') },
      ctx && { label: w('history'), icon: 'history', run: () => ctx.openPanel('history') },
      ctx?.canEdit && ctx.sync && { label: w('saveVersion'), icon: 'check', run: () => ctx.openPanel('history') },
      ctx?.canEdit && ctx.sync && ready && { label: w('tryCopy'), icon: 'branch', run: () => makeEditCopy({ id: ctx.id, sync: ctx.sync, title: ctx.title }) },
      can && { label: 'Insert table', icon: 'table', run: () => ed.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
      can && { label: 'Insert equation', icon: 'equation', run: () => insertEquation(ed, true) },
      can && { label: 'Insert picture', icon: 'image', run: () => addPicture(ed, ctx.id) },
      can && { label: 'Insert checklist', icon: 'checklist', run: () => ed.chain().focus().toggleTaskList().run() },
      can && dev && { label: 'Insert Python block', icon: 'play', run: () => ed.chain().focus().insertContent({ type: 'codeBlock', attrs: { language: 'python' } }).run() },
      can && dev && { label: 'Insert JavaScript block', icon: 'play', run: () => ed.chain().focus().insertContent({ type: 'codeBlock', attrs: { language: 'javascript' } }).run() },
      can && dev && { label: 'Insert SQL block', icon: 'play', run: () => ed.chain().focus().insertContent({ type: 'codeBlock', attrs: { language: 'sql' } }).run() },
      can && dev && { label: 'Insert live sandbox', icon: 'sandbox', run: () => ed.chain().focus().insertContent(newSandbox()).run() },
      ctx?.canEdit && dev && { label: 'Toggle Markdown source', icon: 'markdown', run: () => ctx.toggleSource() },
      ready && { label: 'Open from computer', icon: 'computer', run: () => go('/files') },
      ready && { label: dev ? 'Switch to Casual mode' : 'Switch to Developer mode', icon: dev ? 'pencil' : 'code', run: () => updateProfile({ mode: dev ? 'casual' : 'dev' }).then(() => toast(`${dev ? 'Casual' : 'Developer'} mode on.`)) },
      ready && { label: 'Settings', icon: 'settings', run: () => go('/settings') },
      ready && { label: 'Your docs', icon: 'doc', run: () => go('/') },
      ready && { label: 'Trash', icon: 'trash', run: () => go('/trash') },
    ].filter(Boolean);

    // headings in the open doc
    if (ctx?.sync) {
      const hs = headingsOf(toJSON(ctx.sync.doc));
      for (const h of hs) {
        const s = score(query, h.text);
        if (s) out.push({ group: 'In this doc', label: h.text, icon: 'heading', s: s + 1, run: () => jumpToHeading(ctx.editor, h.index), hint: `H${h.level}` });
      }
    }
    for (const a of actions) { const s = score(query, a.label); if (s) out.push({ group: 'Actions', ...a, s }); }
    if (ready) {
      for (const d of docs.value) {
        if (d.deletedAt) continue;
        const st = score(query, d.title || 'Untitled document');
        const sb = query ? score(query, d.text || d.snippet) : 0;
        if (st || sb) {
          out.push({
            group: 'Docs', label: d.title || 'Untitled document', icon: d.kind === 'copy' ? 'branch' : 'doc', s: st * 2 + sb,
            sub: !st && sb ? snippetAround(d.text || d.snippet, query) : '', run: () => go(`/d/${d.id}`),
          });
        }
      }
    }
    const order = query ? ['Docs', 'In this doc', 'Actions'] : ['Actions', 'In this doc', 'Docs'];
    out.sort((a, b) => order.indexOf(a.group) - order.indexOf(b.group) || b.s - a.s);
    const limited = [];
    const per = {};
    for (const it of out) { per[it.group] = (per[it.group] || 0) + 1; if (per[it.group] <= (query ? 12 : 8)) limited.push(it); }
    return limited;
  }, [q, ctx, docs.value]);

  useEffect(() => setSel(0), [q]);
  useEffect(() => { listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' }); }, [sel]);

  const pick = it => { onClose(); setTimeout(() => it.run(), 0); };
  const onKey = e => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(items.length - 1, s + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(0, s - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (items[sel]) pick(items[sel]); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  let lastGroup = null;
  return (
    <div class="scrim palette-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div class="palette" role="dialog" aria-modal="true" aria-label="Search">
        <div class="palette-input">
          <Icon name="search" size={18} />
          <input ref={input} value={q} onInput={e => setQ(e.target.value)} onKeyDown={onKey}
            placeholder="Search docs, headings and actions…" aria-label="Search" role="combobox" aria-expanded="true"
            aria-controls="palette-list" aria-activedescendant={items[sel] ? `pal-${sel}` : undefined} />
          <kbd>Esc</kbd>
        </div>
        <ul class="palette-list" id="palette-list" role="listbox" ref={listRef}>
          {!items.length && <li class="palette-empty">Nothing matches "{q}".</li>}
          {items.map((it, i) => {
            const head = it.group !== lastGroup ? (lastGroup = it.group) : null;
            return (
              <>
                {head && <li class="palette-group" role="presentation">{head}</li>}
                <li id={`pal-${i}`} role="option" aria-selected={String(i === sel)} class={`palette-item ${i === sel ? 'on' : ''}`}
                  onMouseMove={() => setSel(i)} onClick={() => pick(it)}>
                  <Icon name={it.icon} size={17} />
                  <span class="palette-text"><span>{it.label}</span>{it.sub && <small>{it.sub}</small>}</span>
                  {it.hint && <span class="menu-hint">{it.hint}</span>}
                </li>
              </>
            );
          })}
        </ul>
        <div class="palette-foot"><span><kbd>↑</kbd><kbd>↓</kbd> move</span><span><kbd>Enter</kbd> open</span><span><kbd>Ctrl K</kbd> toggle</span></div>
      </div>
    </div>
  );
}

function jumpToHeading(editor, index) {
  if (!editor) return;
  let n = -1, pos = null;
  editor.state.doc.descendants((node, p) => {
    if (pos !== null) return false;
    if (node.type.name === 'heading' && node.textContent.trim()) { n++; if (n === index) { pos = p; return false; } }
    return true;
  });
  if (pos === null) return;
  editor.chain().focus().setTextSelection(pos + 1).run();
  const dom = editor.view.nodeDOM(pos);
  dom?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
}
